import React, { useState, useEffect } from "react";
import {
  Plus,
  Calendar,
  StickyNote,
  Phone,
  Mail,
  X,
  Send,
  Eye,
  PenLine,
  Inbox,
  AlertCircle,
} from "lucide-react";
import { getActivities, addActivity, deleteActivity } from "../../api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const ACTIVITY_ICONS = {
  phone: Phone,
  calendar: Calendar,
  note: StickyNote,
  email: Mail,
  sent: Send,
  received: Inbox,
  view: Eye,
  sign: PenLine,
  decline: X,
  attention: AlertCircle,
};

const ActivityCard = ({ item, index, onDelete }) => {
  const isFirst = index === 0;
  const Icon = ACTIVITY_ICONS[item.icon] || Calendar;
  const isNudge = typeof item.id === "string" && item.id.startsWith("nudge-");
  return (
    <div className={`relative ${isFirst ? "" : "-mt-[70px]"}`}>
      {/* Top-right delete */}
      <button
        onClick={() => !isNudge && onDelete(item)}
        title={isNudge ? "Resolve to clear" : "Dismiss activity"}
        className={`absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.07] text-black transition-colors hover:bg-black/20 ${
          isNudge ? "cursor-default opacity-40" : ""
        }`}
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>

      {/* Icon tab - rounded top-left, slanted right edge flowing into body */}
      <div className="flex">
        <div
          className="rounded-tl-[22px] pb-3 pl-4 pr-16 pt-4"
          style={{
            backgroundColor: item.bg,
            clipPath:
              "polygon(0 0, calc(100% - 52px) 0, calc(100% - 45px) 2px, calc(100% - 39px) 6px, calc(100% - 34px) 13px, calc(100% - 16px) calc(100% - 9px), calc(100% - 12px) calc(100% - 4px), calc(100% - 6px) calc(100% - 1px), 100% 100%, 0 100%)",
          }}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-black/10 text-black">
            <Icon className="h-5 w-5" strokeWidth={1.8} />
          </span>
        </div>
      </div>

      {/* Body - seamless continuation of the icon tab */}
      <div
        className="-mt-px rounded-b-[22px] rounded-tr-[22px] px-5 pb-12 pt-5 text-black"
        style={{ backgroundColor: item.bg }}
      >
        <div className="flex gap-4">
          <div className="w-11 shrink-0 pt-1 text-center leading-tight">
            <div className="text-[13px] font-semibold">{item.date}</div>
            <div className="text-[11px] text-black/50">{item.time}</div>
          </div>
          <div className="flex-1 pr-8">
            <h3 className="pl-8 text-lg font-bold leading-snug tracking-tight">
              {item.title}
            </h3>
            <div className="mt-3.5 flex items-center gap-2.5 pl-8">
              {item.avatar ? (
                <img
                  src={item.avatar}
                  alt={item.person}
                  className="h-8 w-8 rounded-full border border-black/10 object-cover"
                />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-black/10 text-black">
                  <Icon className="h-4 w-4" strokeWidth={1.9} />
                </span>
              )}
              <p className="text-sm leading-tight">
                <span className="font-semibold">{item.person} </span>
                <span className="text-black/55">{item.subtitle}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActivitySidebar = ({ onAction, dark, company, refreshTick }) => {
  const tabBg = dark ? "rgba(255,255,255,0.09)" : "#FFFFFF";
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    person: "You",
    subtitle: "added an activity",
    date: "Today",
    time: "just now",
    icon: "calendar",
  });

  const loadActs = () =>
    getActivities(company.key)
      .then(setActivities)
      .catch(() => onAction("Failed to load activities"));

  useEffect(() => {
    loadActs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, refreshTick]);

  const quickActions = [
    { icon: Calendar, add: true },
    { icon: StickyNote, add: true },
    { icon: Phone, add: true },
    { icon: Mail, add: true },
  ];

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await addActivity({ ...form, company: company.key });
      setOpen(false);
      setForm({
        title: "",
        person: "You",
        subtitle: "added an activity",
        date: "Today",
        time: "just now",
        icon: "calendar",
      });
      await loadActs();
      onAction("Activity added");
    } catch {
      onAction("Failed to add activity");
    }
  };

  const handleDelete = async (item) => {
    try {
      await deleteActivity(item.id);
      await loadActs();
      onAction("Activity dismissed");
    } catch {
      onAction("Failed to dismiss activity");
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Folder tab strip (mirrored - primary tab on the right) */}
      <div className="relative flex items-end justify-end">
        <div
          className="rounded-tr-[22px] pb-5 pl-14 pr-6 pt-5 md:pr-8"
          style={{
            backgroundColor: tabBg,
            clipPath:
              "polygon(54px 0, 100% 0, 100% 100%, 0 100%, 41px 12px, 44px 6px, 48px 2px)",
          }}
        >
          <h2 className="whitespace-nowrap text-2xl font-bold tracking-tight text-[#1A1815] dark:text-white">
            Activity
          </h2>
        </div>
      </div>

      {/* Panel body - seamless continuation of the Activity tab */}
      <div className="glass-panel -mt-2 flex flex-1 flex-col rounded-b-[22px] rounded-tl-[22px] rounded-tr-none px-6 pb-6 pt-6">
        {/* Quick actions */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {quickActions.map(({ icon: Icon }, i) => (
            <div key={i} className="flex items-center gap-1">
              <button
                onClick={() => onAction("Opening activity")}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:border dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
              >
                <Icon className="h-4 w-4" strokeWidth={1.8} />
              </button>
              <button
                onClick={() => setOpen(true)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:border dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
              >
                <Plus className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </div>
          ))}
        </div>

        {/* Awaiting */}
        <div className="mt-8 flex items-end justify-between">
          <span className="text-sm font-medium text-[#8B8680] dark:text-white/70">Awaiting</span>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold tracking-tight text-[#1A1815] dark:text-white">
              {activities.length}
            </span>
            <span className="mb-1 text-xs text-[#8B8680] dark:text-white/45">Activities</span>
          </div>
        </div>

        {/* Activity cards */}
        <div className="thin-scroll mt-4 flex-1 overflow-y-auto pr-1">
          {activities.map((item, index) => (
            <ActivityCard
              key={item.id}
              item={item}
              index={index}
              onDelete={handleDelete}
            />
          ))}
          {activities.length === 0 && (
            <p className="mt-10 text-center text-sm text-[#8B8680] dark:text-white/40">
              No activities yet.
            </p>
          )}
        </div>
      </div>

      {/* Add activity dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add activity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="act-title">Title</Label>
              <Input
                id="act-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Follow up with signer"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="act-person">Person</Label>
                <Input
                  id="act-person"
                  value={form.person}
                  onChange={(e) => setForm({ ...form, person: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="act-icon">Type</Label>
                <select
                  id="act-icon"
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none"
                >
                  <option value="calendar">Calendar</option>
                  <option value="phone">Call</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="act-date">Date</Label>
                <Input
                  id="act-date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="act-time">Time</Label>
                <Input
                  id="act-time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <button
                type="submit"
                className="rounded-lg bg-[#1A1815] px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95 dark:bg-white dark:text-black"
              >
                Add activity
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivitySidebar;
